// Lógica PURA de la pantalla de carga de la Economía del cliente (`/client/:id/economia`).
//
// Sin React ni i18n adentro (react-router v7 no carga en Jest: todo lo que se testea vive acá).
//
// ⚠️ Las cuentas de este archivo son un ESPEJO de `core/services/client_economics/derivations.py`
// (contrato §2), y existen SÓLO para el feedback inmediato del formulario: «con lo que cargaste ya
// tienes…» mientras el usuario tipea. Lo que se guarda y lo que muestran la card, el Listado y el
// modal lo calcula el backend. Si allá cambia una fórmula, acá cambia con ella: no hay nada que las
// concilie más que el test de los números del artefacto (55 % · 38.000 · 3.000 · 10 % → 37,1 % ·
// 2,7× · 3,6×).

import {SPEND_CAP_MIN_DAYS} from '../../panel_v11/economyFormatters';

export const FORM_FIELDS = [
    'gross_margin_pct',
    'avg_ticket',
    'shipping_per_order',
    'payment_fee_pct',
    'fixed_costs_monthly',
    'agency_fee_monthly',
];

// Factor de seguridad del objetivo sugerido (`SUGGESTED_GOAL_SAFETY_FACTOR` del backend).
export const SUGGESTED_GOAL_SAFETY_FACTOR = 1.3;

// Rangos del contrato §1.1. `min`/`max` exclusivos donde el contrato dice «<», inclusivos donde dice
// «≤». El margen es el ÚNICO obligatorio.
export const FIELD_RULES = {
    gross_margin_pct: {required: true, min: 0, minExclusive: true, max: 100, maxExclusive: true},
    avg_ticket: {min: 0, minExclusive: true},
    shipping_per_order: {min: 0},
    payment_fee_pct: {min: 0, max: 60, maxExclusive: true},
    fixed_costs_monthly: {min: 0},
    agency_fee_monthly: {min: 0},
};

// Vigencia admitida por el backend: de un año atrás a mañana (contrato §3.2).
export const EFFECTIVE_FROM_MAX_DAYS_BACK = 365;
export const EFFECTIVE_FROM_MAX_DAYS_AHEAD = 1;

export const UNLOCK_READY = 'ready';
export const UNLOCK_PENDING = 'pending';

/**
 * Un número tipeado en formato argentino o con punto decimal: «38.000», «10,5», «1.234,56», «10.5».
 * Vacío → `null` (el campo no se cargó). Inválido → `NaN` (el campo se marca).
 *
 * La MISMA regla que el importador del backend (`import_service.parse_number`), así el formulario y la
 * planilla leen igual un mismo texto:
 * - con punto Y coma, el separador de más a la derecha es el decimal: «1.234,56» → 1234,56 y
 *   «900,000.00» (pegado de una planilla en inglés) → 900000 — no 900 (R-ecofe-03);
 * - comas que agrupan de a tres sin decimal («38,000»): AMBIGUO (¿38 o 38.000?), inválido — el
 *   campo se marca y el usuario lo escribe sin dudas;
 * - sólo coma: es el decimal y los puntos se caen;
 * - sólo puntos: separador de miles si agrupa de a tres («38.000», «1.250.000»); si no, es el decimal
 *   de quien tipea con teclado en inglés («10.5», «0.500»).
 */
const AMBIGUOUS_THOUSANDS_WITH_COMMAS = /^-?[1-9]\d{0,2}(,\d{3})+$/;
const THOUSANDS_WITH_DOTS = /^-?[1-9]\d{0,2}(\.\d{3})+$/;
const NOT_NUMBER_CHARS = /[\s$%]|ARS|USD|CLP|MXN|COP|PEN|UYU|EUR/gi;

export function parseLocaleNumber(text) {
    if (text === null || text === undefined) return null;
    if (typeof text === 'number') return Number.isFinite(text) ? text : NaN;
    const raw = String(text).replace(NOT_NUMBER_CHARS, '');
    if (raw === '') return null;
    if (AMBIGUOUS_THOUSANDS_WITH_COMMAS.test(raw)) return NaN;
    let normalized = raw;
    if (raw.includes('.') && raw.includes(',')) {
        normalized = raw.lastIndexOf(',') > raw.lastIndexOf('.')
            ? raw.replace(/\./g, '').replace(',', '.')
            : raw.replace(/,/g, '');
    } else if (raw.includes(',')) {
        normalized = raw.replace(/\./g, '').replace(',', '.');
    } else if (THOUSANDS_WITH_DOTS.test(raw)) {
        normalized = raw.replace(/\./g, '');
    }
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return NaN;
    return Number(normalized);
}

/**
 * `round(value, digits)` de Python: mitad AL PAR sobre el valor binario exacto. El backend redondea
 * así al serializar (ROAS y % a 1 decimal) y el formulario tiene que decir lo mismo que la card: con
 * `Math.round` (mitad hacia arriba) un margen del 80 % daba 1,3× acá y 1,2× allá (R-ecofe-11).
 *
 * La decisión se toma sobre la expansión decimal del double (`toFixed` con 30 dígitos de más), no
 * sobre `value * 10^digits`, que redondea en la multiplicación: `1.15 * 10` da 11,5 exacto aunque
 * 1,15 es 1,1499… en binario (y Python da 1,1).
 */
export function pyRound(value, digits = 0) {
    if (value === null || value === undefined || !Number.isFinite(value)) return value;
    const negative = value < 0;
    const text = Math.abs(value).toFixed(Math.min(100, digits + 30));
    const [intPart, fracPart = ''] = text.split('.');
    const kept = fracPart.slice(0, digits);
    const tail = fracPart.slice(digits);
    const base = Number(digits > 0 ? `${intPart}.${kept}` : intPart);
    let up = false;
    if (tail[0] > '5') {
        up = true;
    } else if (tail[0] === '5') {
        const rest = tail.slice(1);
        if (/[1-9]/.test(rest)) {
            up = true;
        } else {
            // Empate exacto: al par.
            const lastDigit = Number((digits > 0 ? kept : intPart).slice(-1));
            up = lastDigit % 2 === 1;
        }
    }
    const step = 10 ** -digits;
    const rounded = Number((up ? base + step : base).toFixed(digits));
    return negative ? -rounded : rounded;
}

/** Un número del backend listo para un `<input>` (formato argentino, sin redondear decimales). */
export function formatInputNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return number.toLocaleString('es-AR', {maximumFractionDigits: 2});
}

function pad(number) {
    return String(number).padStart(2, '0');
}

/** `YYYY-MM-DD` del día LOCAL (el backend usa la fecha de la agencia, no UTC). */
export function todayIso(now = new Date()) {
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function isoPlusDays(iso, days) {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d + days);
    return todayIso(date);
}

/**
 * Los valores del formulario a partir del perfil vigente. El ticket arranca con el CALCULADO de la
 * tienda si el perfil no trae uno propio: el usuario lo ve precargado y lo corrige si quiere — pero
 * si no lo toca, se guarda como `null` («calculado de la tienda»), que es lo que el backend sigue
 * recalculando. Por eso `avgTicketTouched` viaja aparte.
 */
export function valuesFromProfile(data, now = new Date()) {
    const profile = (data && data.profile) || {};
    const computed = (data && data.avg_ticket_computed) || {};
    const hasOwnTicket = profile.avg_ticket !== null && profile.avg_ticket !== undefined;
    return {
        gross_margin_pct: formatInputNumber(profile.gross_margin_pct),
        avg_ticket: hasOwnTicket ? formatInputNumber(profile.avg_ticket) : formatInputNumber(computed.value),
        avgTicketTouched: hasOwnTicket,
        shipping_per_order: formatInputNumber(profile.shipping_per_order),
        payment_fee_pct: formatInputNumber(profile.payment_fee_pct),
        fixed_costs_monthly: formatInputNumber(profile.fixed_costs_monthly),
        agency_fee_monthly: formatInputNumber(profile.agency_fee_monthly),
        currency: (data && data.currency) || profile.currency || defaultCurrency(data) || '',
        effective_from: todayIso(now),
        note: '',
    };
}

/**
 * ¿El usuario cambió algo respecto del perfil vigente? Un recargo por otra vía (planilla, Escalafy,
 * una línea) no puede pisar lo que se estaba tipeando (R-ecofe-28): con el formulario sucio, la
 * pantalla actualiza el dato y deja los valores como están.
 */
export function formIsDirty(values, data, now = new Date()) {
    if (!values) return false;
    const initial = valuesFromProfile(data, now);
    return [...FORM_FIELDS, 'currency', 'effective_from', 'note']
        .some((field) => String(values[field] ?? '') !== String(initial[field] ?? ''));
}

/** La moneda por default: la de la primera línea con tienda, o la de la primera línea (§1.1). */
export function defaultCurrency(data) {
    const lines = (data && Array.isArray(data.lines)) ? data.lines : [];
    const withStore = lines.find((line) => line && line.has_store && line.currency);
    if (withStore) return withStore.currency;
    const first = lines.find((line) => line && line.currency);
    return first ? first.currency : null;
}

// Monedas de respaldo cuando ni el perfil ni las líneas dicen una (cliente sin líneas o sin presupuesto):
// sin esto, el selector sólo ofrecía «—» y la primera versión no se podía guardar (R-ecofe-13). Son las
// de los países donde opera Dashbo.
export const FALLBACK_CURRENCIES = ['ARS', 'USD', 'EUR', 'BRL', 'CLP', 'COP', 'MXN', 'PEN', 'UYU'];

/** Las monedas que tiene sentido ofrecer: la del perfil y las de las líneas, sin repetir. */
export function currencyOptions(data, current) {
    const set = new Set();
    [current, data && data.currency, ((data && data.profile) || {}).currency]
        .forEach((currency) => { if (currency) set.add(currency); });
    ((data && data.lines) || []).forEach((line) => { if (line && line.currency) set.add(line.currency); });
    if (!set.size) FALLBACK_CURRENCIES.forEach((currency) => set.add(currency));
    return [...set];
}

function outOfRange(value, rule) {
    if (rule.min !== undefined && (rule.minExclusive ? value <= rule.min : value < rule.min)) return true;
    if (rule.max !== undefined && (rule.maxExclusive ? value >= rule.max : value > rule.max)) return true;
    return false;
}

/**
 * Qué campos están mal, con las reglas del contrato. Devuelve `{valid, fields}` donde `fields` es
 * un set de nombres de campo marcados (el copy dice «revisa los campos marcados»: la pantalla los
 * resalta, no escribe un mensaje por campo).
 */
export function validateEconomics(values, now = new Date()) {
    const fields = {};
    FORM_FIELDS.forEach((field) => {
        const rule = FIELD_RULES[field];
        const parsed = parseLocaleNumber(values[field]);
        if (parsed === null) {
            if (rule.required) fields[field] = true;
            return;
        }
        if (Number.isNaN(parsed) || outOfRange(parsed, rule)) fields[field] = true;
    });
    if (!values.currency || !/^[A-Z]{3}$/.test(String(values.currency))) fields.currency = true;
    const effective = values.effective_from;
    const today = todayIso(now);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(effective || ''))
        || effective > isoPlusDays(today, EFFECTIVE_FROM_MAX_DAYS_AHEAD)
        || effective < isoPlusDays(today, -EFFECTIVE_FROM_MAX_DAYS_BACK)) {
        fields.effective_from = true;
    }
    return {valid: Object.keys(fields).length === 0, fields};
}

/**
 * Los campos que el backend rechazó, SÓLO si el error es de validación (`INVALID_ECONOMICS`, con
 * `fields`). Cualquier otro —403 del módulo, 404, red— devuelve `null`: decir «revisa los campos
 * marcados» sin ningún campo marcado deja al usuario sin salida (R-ecofe-15).
 */
export function invalidFieldsOf(response) {
    const body = (response && response.raw_body) || {};
    const code = body.error_code || (response && response.error_code);
    if (code !== 'INVALID_ECONOMICS') return null;
    return Object.keys(body.fields || {}).reduce((acc, key) => ({...acc, [key]: true}), {});
}

/**
 * El body del POST (§3.2 / §3.3). El ticket va `null` si el usuario no lo tocó y seguía el
 * calculado: así el backend lo sigue calculando de la tienda en vez de congelar el de hoy.
 */
export function toPayload(values) {
    const number = (field) => parseLocaleNumber(values[field]);
    return {
        gross_margin_pct: number('gross_margin_pct'),
        avg_ticket: values.avgTicketTouched ? number('avg_ticket') : null,
        shipping_per_order: number('shipping_per_order'),
        payment_fee_pct: number('payment_fee_pct'),
        fixed_costs_monthly: number('fixed_costs_monthly'),
        agency_fee_monthly: number('agency_fee_monthly'),
        currency: values.currency,
        effective_from: values.effective_from,
        note: values.note || '',
    };
}

/**
 * Margen de contribución (fracción 0-1) y por qué no se pudo: `cm = m − e/t − f` (§2). Sin envío no
 * hace falta el ticket; con envío y sin ticket (ni cargado ni calculado) no hay cuenta posible.
 */
export function contributionMargin({margin, ticket, shipping, fee}) {
    if (margin === null || !Number.isFinite(margin)) return {value: null, reason: 'missing_input:gross_margin_pct'};
    const shippingValue = Number.isFinite(shipping) ? shipping : 0;
    const feeValue = Number.isFinite(fee) ? fee : 0;
    let shippingShare = 0;
    if (shippingValue > 0) {
        if (!Number.isFinite(ticket) || ticket <= 0) return {value: null, reason: 'missing_input:avg_ticket'};
        shippingShare = shippingValue / ticket;
    }
    // Redondeo a 12 decimales, como `derivations.contribution_margin`: `0,20 − 0,15 − 0,05` da
    // 1,39e-17, que pasaría como margen positivo con un equilibrio de 7e16× (K-ecofe-03).
    return {value: pyRound(margin / 100 - shippingShare - feeValue / 100, 12), reason: null};
}

/** ROAS de equilibrio `1 / cm`; `null` si el margen no es positivo (`NON_POSITIVE_MARGIN`). */
export function breakevenRoas(cm) {
    if (cm === null || !Number.isFinite(cm) || cm <= 0) return null;
    return 1 / cm;
}

/**
 * Objetivo sugerido: `ceil_to_tenth(equilibrio × 1,3)` sobre el equilibrio SIN redondear (§2), con el
 * MISMO blindaje contra el ruido del float que el backend: `ceil(round(x * 10, 6)) / 10`.
 */
export function suggestedGoalRoas(breakeven) {
    if (breakeven === null || !Number.isFinite(breakeven)) return null;
    return Math.ceil(pyRound(breakeven * SUGGESTED_GOAL_SAFETY_FACTOR * 10, 6)) / 10;
}

/** Los derivados locales con los redondeos del serializador (% y ROAS a 1 decimal). */
export function localDerivations(values, data) {
    const computed = (data && data.avg_ticket_computed) || {};
    const typedTicket = parseLocaleNumber(values.avg_ticket);
    const ticket = Number.isFinite(typedTicket) && typedTicket !== null ? typedTicket : Number(computed.value);
    const cm = contributionMargin({
        margin: parseLocaleNumber(values.gross_margin_pct),
        ticket,
        shipping: parseLocaleNumber(values.shipping_per_order),
        fee: parseLocaleNumber(values.payment_fee_pct),
    });
    const breakeven = breakevenRoas(cm.value);
    return {
        contributionMarginPct: cm.value === null ? null : pyRound(cm.value * 100, 1),
        breakevenRoas: breakeven === null ? null : pyRound(breakeven, 1),
        suggestedGoalRoas: suggestedGoalRoas(breakeven),
        reason: cm.reason || (breakeven === null ? 'NON_POSITIVE_MARGIN' : null),
    };
}

function hasNumber(values, field) {
    const parsed = parseLocaleNumber(values[field]);
    return parsed !== null && Number.isFinite(parsed);
}

/**
 * Lo que habilita cada dato cargado («Con lo que cargaste, ya tienes»): la misma escalera del
 * contrato — el margen da el equilibrio; envío y pasarela afinan el margen de contribución; fijos y
 * honorarios dan la ganancia (si alguna línea tiene tienda); el tope pide 30 días de histórico.
 *
 * `capDaysMissing`: cuántos días le faltan a la línea más avanzada (`null` = no se sabe, o alguna
 * línea ya lo tiene → la fila del tope no se dibuja: decir «faltan N» sin saber N sería inventarlo, y
 * el copy sólo tiene la versión pendiente).
 */
export function unlocksFor(values, data, {capDaysMissing = null} = {}) {
    const derived = localDerivations(values, data);
    const hasMargin = hasNumber(values, 'gross_margin_pct') && derived.breakevenRoas !== null;
    const refined = hasMargin && (hasNumber(values, 'shipping_per_order') || hasNumber(values, 'payment_fee_pct'));
    const hasCosts = hasNumber(values, 'fixed_costs_monthly') || hasNumber(values, 'agency_fee_monthly');
    const linesWithStore = ((data && data.lines) || []).filter((line) => line && line.has_store).length;
    // Con envío cargado y sin ticket (ni tipeado ni calculado de la tienda), el envío no se puede
    // llevar a «por venta»: no hay margen de contribución y por lo tanto tampoco equilibrio. Las
    // filas lo dicen, en vez de prometer que el equilibrio «sale solo con el margen» (QA-eco-N02).
    const needsTicket = derived.reason === 'missing_input:avg_ticket';

    const rows = [
        {
            key: 'breakeven',
            status: hasMargin ? UNLOCK_READY : UNLOCK_PENDING,
            textKey: needsTicket ? 'economics.unlock.breakeven_needs_ticket' : 'economics.unlock.breakeven',
            value: derived.breakevenRoas,
            kind: 'roas',
        },
        {
            key: 'contribution',
            status: refined && derived.contributionMarginPct !== null ? UNLOCK_READY : UNLOCK_PENDING,
            textKey: needsTicket ? 'economics.unlock.contribution_needs_ticket' : 'economics.unlock.contribution',
            value: derived.contributionMarginPct,
            kind: 'pct',
        },
        {
            key: 'profit',
            status: hasMargin && hasCosts && linesWithStore > 0 ? UNLOCK_READY : UNLOCK_PENDING,
            textKey: linesWithStore > 0 ? 'economics.unlock.profit' : 'economics.unlock.profit_no_store',
        },
    ];
    if (capDaysMissing !== null && capDaysMissing !== undefined && capDaysMissing > 0) {
        rows.push({key: 'cap', status: UNLOCK_PENDING, textKey: 'economics.unlock.cap_pending', days: capDaysMissing});
    }
    return rows;
}

/**
 * Cuántos días de histórico le faltan al tope, mirando el `derived` de cada línea si el backend lo
 * manda (`TOO_FEW_DAYS:<n>`): el de la línea más cercana a los 30. `ready` si alguna ya lo tiene
 * (y entonces `missing` no importa). Sin información, `{ready:false, missing:null}`.
 */
export function spendCapProgress(data) {
    const lines = ((data && data.lines) || []).filter((line) => line && line.derived);
    let ready = false;
    let missing = null;
    lines.forEach((line) => {
        const derived = line.derived;
        if (derived.spend_cap !== null && derived.spend_cap !== undefined) ready = true;
        const reason = derived.unavailable_reason && derived.unavailable_reason.spend_cap;
        const [code, detail] = String(reason || '').split(':');
        if (code === 'TOO_FEW_DAYS' && Number.isFinite(Number(detail))) {
            const lineMissing = Math.max(0, SPEND_CAP_MIN_DAYS - Number(detail));
            missing = missing === null ? lineMissing : Math.min(missing, lineMissing);
        }
    });
    return {ready, missing};
}

/**
 * ¿Ya hay una versión ACTIVA con esa vigencia? Guardar con la misma fecha la reemplaza (es una
 * corrección, no historia), y la pantalla lo avisa antes.
 */
export function replacesSameDate(effectiveFrom, data, versions = null) {
    if (!effectiveFrom) return false;
    const profile = (data && data.profile) || null;
    if (profile && profile.effective_from === effectiveFrom) return true;
    return Array.isArray(versions) && versions.some((version) => version && version.effective_from === effectiveFrom);
}
